/* eslint-disable no-useless-computed-key */
import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { DropZone, Button } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  setModalxComponent,
  setModalxComponentProps,
  setDestroy,
} from "components/modalx/modalx.slice";
import {
  upload,
  replaceFile,
  patchFile,
  isEmptyUploadError,
} from "services/upload-file.service";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchFinanceDetailRefreshAsync } from "containers/finance-detail/finance-detail-container.slice";

function UploadFile({
  onChange,
  subject,
  label = "",
  subjectId,
  categoryId,
  isCustomerSelectorVisible,
  parentFileId,
  categoryType,
  isUserSelectorVisible,
  source,
  sanctionsType,
  isVatCheckDocument,
  year,
  fileCategory,
  fileType,
  customerFinance,
}) {
  const dispatch = useDispatch();
  const { companyId, projectId, customerId } = useParams();
  const currentUserId = useSelector((state) => state.authReducer?.role?.user);

  const [openFileDialog, setOpenFileDialog] = useState(false);
  const { t } = useTranslation();

  const canAutoPatch =
    subject === "customer" &&
    subjectId &&
    fileCategory &&
    fileType &&
    currentUserId &&
    (sanctionsType || isVatCheckDocument);

  const handleDropZoneDrop = useCallback(
    // eslint-disable-next-line no-unused-vars
    async (acceptedFiles) => {
      const uploadedFiles = [];
      try {
        for (let file of acceptedFiles) {
          dispatch(setModalxComponent("Loading"));
          const { data } = await upload(file, companyId);
          uploadedFiles.push(data);
        }
        if (uploadedFiles.length === 1) {
          if (subject === "replace") {
            const payload = {
              prevId: parentFileId,
              nextId: uploadedFiles[0],
              customerId,
            };
            await replaceFile(companyId, payload);
            dispatch(fetchCustomer({ companyId, customerId }));
            dispatch(setDestroy());
            return;
          }

          if (canAutoPatch) {
            const dateStr = new Date().toISOString().split("T")[0];
            const patchPayload = {
              id: uploadedFiles[0],
              categories: [fileCategory],
              fileType,
              fileTypeContext: "",
              customer: [subjectId],
              date: dateStr,
              assignedUser: currentUserId,
              checkSum: true,
              year,
              ...(sanctionsType && { sanctionsType }),
              ...(isVatCheckDocument && { isVatCheckDocument: true }),
              ...(customerFinance && { customerFinance }),
            };
            await patchFile(patchPayload, companyId);
            if (year != null) {
              dispatch(
                fetchFinanceDetailRefreshAsync({
                  companyId,
                  customerId: subjectId,
                  year,
                  financeId: customerFinance,
                })
              );
            } else {
              dispatch(fetchCustomer({ companyId, customerId: subjectId }));
            }
            dispatch(setDestroy());
            if (onChange) onChange();
            return;
          }

          dispatch(setModalxComponent("FileForm"));
          dispatch(
            setModalxComponentProps({
              fileId: uploadedFiles[0],
              companyId,
              projectId,
              categoryId,
              isCustomerSelectorVisible,
              parentFileId,
              categoryType,
              customerId,
              subject,
              subjectId,
              isCategoryRequired: !parentFileId,
              isUserSelectorVisible,
              source,
              checksum: true,
              sanctionsType,
              isVatCheckDocument,
              year,
              ...(customerFinance && { customerFinance }),
            })
          );
        }
      } catch (e) {
        // Close the Loading modal either way — it must never hang open.
        // The empty-file case is already toasted by the service; anything
        // else still needs its own message here.
        dispatch(setDestroy());
        if (!isEmptyUploadError(e)) {
          toast.error(t("error"));
        }
      }
    },
    [
      companyId,
      dispatch,
      subject,
      subjectId,
      categoryId,
      isCustomerSelectorVisible,
      parentFileId,
      categoryType,
      projectId,
      customerId,
      isUserSelectorVisible,
      sanctionsType,
      isVatCheckDocument,
      year,
      canAutoPatch,
      fileCategory,
      fileType,
      currentUserId,
      onChange,
      customerFinance,
      t,
    ]
  );
  const toggleOpenFileDialog = useCallback(
    // eslint-disable-next-line no-shadow
    () => setOpenFileDialog((openFileDialog) => !openFileDialog),
    []
  );

  return (
    <>
      <Button onClick={toggleOpenFileDialog}>{label || t("upload")}</Button>
      <div style={{ display: "none" }}>
        <DropZone
          allowMultiple={false}
          openFileDialog={openFileDialog}
          onDropAccepted={handleDropZoneDrop}
          onFileDialogClose={toggleOpenFileDialog}
        >
          <DropZone.FileUpload />
        </DropZone>
      </div>
    </>
  );
}

export default UploadFile;

UploadFile.propTypes = {
  onChange: PropTypes.func.isRequired,
  isFileInputs: PropTypes.bool,
  subjectId: PropTypes.string,
  subject: PropTypes.string,
  categoryId: PropTypes.string,
  isCustomerSelectorVisible: PropTypes.bool,
  parentFileId: PropTypes.string,
  isUserSelectorVisible: PropTypes.bool,
  source: PropTypes.string,
  sanctionsType: PropTypes.string,
  isVatCheckDocument: PropTypes.bool,
  year: PropTypes.number,
  fileCategory: PropTypes.string,
  fileType: PropTypes.string,
  customerFinance: PropTypes.string,
};

UploadFile.defaultProps = {
  isUserSelectorVisible: false,
  isCustomerSelectorVisible: false,
  parentFileId: null,
};
